import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Conversation, SchemaType, ToolVertex } from '../@types/IA.type';
import {
	getChatsAI,
	chatAgentAI,
	getMessageRecommendations,
} from '../services/heru-ai.service';
import STORAGE_KEYS from '../shared/constants/storage';
import { useStorage } from '../shared/utils/general.util';
import { RootState } from '../redux/store';
import { ALERT_TYPE } from '../components/molecules/Alert';
import { showAlert } from '../redux/slices/alert.slice';
import { ChatAgent } from '../@types/EnumsAI.enum';

const useChatAI = ({
	initialMessage,
	agentName = ChatAgent.WEB_CHAT_ORCHESTRATOR,
	showLastChat = false,
	canClose = false,
}: {
	initialMessage: Conversation;
	agentName?: ChatAgent;
	showLastChat?: boolean;
	canClose?: boolean;
}) => {
	const dispatch = useDispatch();
	const { status: taxpayer } = useSelector(
		(state: RootState) => state.satLinkStatus,
	);
	const isLinked = taxpayer?.status === 'ACTIVE';
	const { user: userProfile } = useSelector(
		(state: RootState) => state.profile,
	);
	const messagesRef = useRef<HTMLDivElement>(null);
	const [currentThread, setCurrentThread] =
		useState<Conversation>(initialMessage);
	const [isLoading, setIsLoading] = useState({
		completion: false,
		history: false,
	});
	const [chatHistory, setChatHistory] = useState<Conversation[]>([]);
	const [showMessages, setShowMessages] = useState(!canClose);
	const [userMessage, setUserMessage] = useState('');
	const [messageRecommendations, setMessageRecommendations] = useState<
		{ label: string; message: string }[]
	>([]);
	const { tokenSession } = useSelector((state: RootState) => state.auth);

	const fetchMessageRecommendations = async () => {
		try {
			const response = await getMessageRecommendations(agentName, tokenSession);
			if (response && response.length > 0) {
				setMessageRecommendations(response);
			}
		} catch (error) {}
	};

	const toolsAllowed = (() => {
		const output: ToolVertex[] = [];

		if (isLinked) {
			// output.push({
			// 	agent: ChatAgent.WEB_CHAT_SUPPORT_V1,
			// 	name: 'crear_declaracion_complementaria',
			// 	parameters: {
			// 		type: SchemaType.OBJECT,
			// 		required: ['year', 'month'],
			// 		properties: {
			// 			year: {
			// 				type: SchemaType.INTEGER,
			// 				description: 'Año de la declaración',
			// 			},
			// 			month: {
			// 				type: SchemaType.INTEGER,
			// 				description: 'Mes de la declaración',
			// 			},
			// 		},
			// 	},
			// 	description:
			// 		'Genera un link de linea de pago en caso de que al usuario no le haya dado tiempo a pagar la anterior linea de pago de la declaración mensual.',
			// });
		}

		output.push({
			agentName: ChatAgent.WEB_CHAT_SUPPORT,
			name: 'acuse_declaracion_presentada',
			parameters: {
				type: SchemaType.OBJECT,
				required: ['year', 'type'],
				properties: {
					type: {
						type: SchemaType.STRING,
						description:
							"Si menciona el mes es 'mensual' si solo dice el año es 'anual'",
					},
					year: {
						type: SchemaType.INTEGER,
						description: 'Año de la declaración',
					},
					month: {
						type: SchemaType.INTEGER,
						description: 'Mes de la declaración',
					},
				},
			},
			description:
				'Obtiene el link del acuse de la declaración presentada por el usuario, para visualizar o descargarlo desde el chat',
		});

		output.push({
			agentName: ChatAgent.WEB_CHAT_SUPPORT,
			name: 'form_hablar_con_agente_soporte',
			parameters: {
				type: SchemaType.OBJECT,
				required: ['conversationSummary'],
				properties: {
					conversationSummary: {
						type: SchemaType.STRING,
						description:
							'Detalle del problema que el usuario está teniendo o el asunto que el usuario quiere resolver con un agente de soporte. Si no es claro, pregunta al usuario para que te de una descripción más clara del asunto. Restricción: Evita lanzar está función varias veces seguidas.',
					},
				},
			},
			description:
				'Ejecuta esta funcion cuando el usuario parece molesto o te pide hablar con un humano.',
		});

		return output;
	})();

	const getMessageHistory = async (
		userId?: number,
		refresh: boolean = true,
	) => {
		try {
			if (!userId) return;
			if (refresh) {
				setIsLoading((prev) => ({
					...prev,
					history: true,
				}));
			}
			const messageHistory = await getChatsAI(userId);
			setChatHistory(messageHistory);
		} catch (error) {
			dispatch(
				showAlert({
					type: ALERT_TYPE.WARNING,
					title: 'Aviso',
					description: 'Error al obtener el historial de chats',
				}),
			);
		} finally {
			setIsLoading((prev) => ({
				...prev,
				history: false,
			}));
		}
	};

	const handleNewChat = async () => {
		setCurrentThread(initialMessage);
		setUserMessage('');
		useStorage().setItem(STORAGE_KEYS.LAST_CHAT_AI, '0');
		getMessageHistory(userProfile?.id, false);
	};

	const handleSubmit = async (
		message: string,
		e?: React.FormEvent<HTMLFormElement | HTMLTextAreaElement>,
	) => {
		e?.preventDefault();
		try {
			if (message.trim() === '' || !userProfile?.id || isLoading.completion)
				return;
			setIsLoading((prev) => ({
				...prev,
				completion: true,
			}));
			setCurrentThread((prev) => ({
				...prev,
				thread: [...prev.thread, { role: 'user', content: message }],
			}));
			setUserMessage('');

			const additionalInformation = `- UserName: ${userProfile?.first_name} ${userProfile?.father_surname}
- User email: ${userProfile?.email}`;
			const isNewThread = currentThread?.thread?.length <= 1;
			const thread = isNewThread
				? [...initialMessage.thread, { role: 'user', content: message }]
				: [...currentThread.thread, { role: 'user', content: message }];

			const responseAI = await chatAgentAI({
				userId: userProfile?.id,
				thread,
				additionalInfo: additionalInformation,
				tools: toolsAllowed,
				threadId: isNewThread ? undefined : currentThread.id,
				agentName,
				tokenSession,
			});

			if (!responseAI || !responseAI.thread) {
				throw new Error('Error al obtener la respuesta');
			}
			setCurrentThread(responseAI);
			useStorage().setItem(
				STORAGE_KEYS.LAST_CHAT_AI,
				responseAI?.id.toString(),
			);
		} catch (error) {
			const alert = {
				type: ALERT_TYPE.WARNING,
				title: 'Aviso',
				description: 'Error al enviar el mensaje, intenta nuevamente.',
			};
			dispatch(showAlert(alert));
		} finally {
			setIsLoading((prev) => ({
				...prev,
				completion: false,
			}));
		}
	};

	useEffect(() => {
		if (currentThread.thread.length > 0 && messagesRef.current) {
			setTimeout(() => {
				if (messagesRef.current) {
					messagesRef.current.scrollTo({
						top: messagesRef.current.scrollHeight,
						behavior: 'smooth',
					});
				}
			}, 100);
		}
	}, [currentThread]);

	useEffect(() => {
		if (chatHistory.length > 0) {
			const orderedChatHistory = chatHistory.sort((a, b) => {
				return (
					new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
				);
			});
			if (showLastChat) {
				const lastChatFromStorage = useStorage().getItem(
					STORAGE_KEYS.LAST_CHAT_AI,
				);
				if (lastChatFromStorage === '0') {
					return;
				}
				const lastChat = orderedChatHistory.find(
					(chat) => chat.id === parseInt(lastChatFromStorage || '0'),
				);
				if (lastChat) {
					setCurrentThread(lastChat);
				} else {
					setCurrentThread(orderedChatHistory[0]);
				}
			}
		}
	}, [chatHistory]);

	useEffect(() => {
		getMessageHistory(userProfile?.id);
	}, [userProfile?.id]);

	useEffect(() => {
		fetchMessageRecommendations();
	}, [agentName]);

	return {
		currentThread,
		chatHistory,
		handleNewChat,
		getMessageHistory,
		showMessages,
		setShowMessages,
		isLoading,
		setIsLoading,
		messagesRef,
		setCurrentThread,
		handleSubmit,
		userMessage,
		setUserMessage,
		messageRecommendations,
	};
};

export default useChatAI;
